import { useReducer } from 'react';
import { useTranslation } from 'react-i18next';

import { useAnalytics } from '../analytics';
import { useUser } from '../context/User';

export const PHONE_LENGTH_MIN = 8;
export const PHONE_LENGTH_MAX = 15;
export const CODE_LENGTH = 6;

type VIEW_STATE =
  | 'ENTER_PHONE_NUMBER'
  | 'ENTER_VERIFICATION_CODE'
  | 'PHONE_NUMBER_ALREADY_USED'
  | 'PHONE_NUMBER_VALIDATED';

interface PhoneVerificationState {
  phoneNumber: string;
  viewState: VIEW_STATE;
  isLoading: boolean;
  verificationCode: string;
  errorMessage: string | null;
}

type PhoneVerificationAction =
  | {
      type: 'UPDATE_FIELD';
      field: 'phoneNumber' | 'verificationCode';
      value: string;
    }
  | { type: 'START_REQUEST' }
  | { type: 'FINISH_REQUEST'; error?: string; viewState?: VIEW_STATE };

const initialState: PhoneVerificationState = {
  phoneNumber: '',
  viewState: 'ENTER_PHONE_NUMBER',
  isLoading: false,
  verificationCode: '',
  errorMessage: null,
};

const phoneVerificationReducer = (
  state: PhoneVerificationState,
  action: PhoneVerificationAction,
): PhoneVerificationState => {
  switch (action.type) {
    case 'UPDATE_FIELD':
      return { ...state, [action.field]: action.value };
    case 'START_REQUEST':
      return { ...state, isLoading: true, errorMessage: null };
    case 'FINISH_REQUEST':
      return {
        ...state,
        isLoading: false,
        errorMessage: action.error ?? null,
        ...(action.viewState ? { viewState: action.viewState } : {}),
      };
  }
};

interface UsePhoneNumberVerificationProps {
  validatePhoneNumber: (phoneNumber: string) => Promise<boolean>;
}

export const usePhoneNumberVerification = ({
  validatePhoneNumber,
}: UsePhoneNumberVerificationProps) => {
  const { t } = useTranslation();
  const { sendPhoneNumber, confirmPhoneNumber, verifyPhoneNumber } = useUser();
  const analytics = useAnalytics();
  const [state, dispatch] = useReducer(phoneVerificationReducer, initialState);

  const setPhoneNumber = (phoneNumber: string) => {
    dispatch({
      type: 'UPDATE_FIELD',
      field: 'phoneNumber',
      value: phoneNumber,
    });
  };

  const setVerificationCode = (code: string) => {
    dispatch({ type: 'UPDATE_FIELD', field: 'verificationCode', value: code });
  };

  const handleSendPhoneNumber = async () => {
    analytics.trackPhoneVerification.sendPhone();
    dispatch({ type: 'START_REQUEST' });

    try {
      const isValidPhone = await validatePhoneNumber(state.phoneNumber);
      if (isValidPhone) {
        await sendPhoneNumber(state.phoneNumber);
        dispatch({
          type: 'FINISH_REQUEST',
          viewState: 'ENTER_VERIFICATION_CODE',
        });
      } else {
        dispatch({
          type: 'FINISH_REQUEST',
          viewState: 'PHONE_NUMBER_ALREADY_USED',
        });
      }
    } catch {
      dispatch({
        type: 'FINISH_REQUEST',
        error: t(
          'phone_number.validation.error',
          'Problem with validating phone number, please try again.',
        ),
      });
    }
  };

  const handleVerificationCode = async () => {
    analytics.trackPhoneVerification.verifyCode();
    dispatch({ type: 'START_REQUEST' });

    try {
      await confirmPhoneNumber(state.verificationCode);
      analytics.trackPhoneVerification.verificationSucceeded();
      dispatch({
        type: 'FINISH_REQUEST',
        viewState: 'PHONE_NUMBER_VALIDATED',
      });
    } catch {
      analytics.trackPhoneVerification.verificationFailed();
      dispatch({
        type: 'FINISH_REQUEST',
        error: t(
          'phone_number.verification_code.error',
          'Problem with validating verification code, please check again.',
        ),
      });
    }
  };

  const handleResendVerificationCode = async () => {
    dispatch({ type: 'START_REQUEST' });

    try {
      await verifyPhoneNumber();
      dispatch({ type: 'FINISH_REQUEST' });
    } catch {
      dispatch({
        type: 'FINISH_REQUEST',
        error: t(
          'phone_number.resend_verification.error',
          'Problem with resending verification code, please try again.',
        ),
      });
    }
  };

  return {
    phoneNumber: state.phoneNumber,
    viewState: state.viewState,
    isLoading: state.isLoading,
    verificationCode: state.verificationCode,
    errorMessage: state.errorMessage,
    setPhoneNumber,
    setVerificationCode,
    handleSendPhoneNumber,
    handleVerificationCode,
    handleResendVerificationCode,
  };
};
